import React from 'react';
import { ChevronLeft, MoreVertical, Zap, Clock, Users, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SkillsSection from './SkillsSection';
import CandidatesInviteSection from './CandidatesInviteSection';
import SettingsSection from './SettingsSection';

interface AssessmentDetailProps {
  selectedAssessment: any;
  handleBackToAssessments: () => void;
  activeSidebarItem: 'skills' | 'candidates' | 'settings';
  setActiveSidebarItem: (item: 'skills' | 'candidates' | 'settings') => void;
  skills: Array<{ _id: string; name: string; timeLimit: number; difficulty: string; hasQuestions: boolean; questions?: Array<{ id: string; text: string }> }>;
  openEditSkillModal: (skill: any) => void;
  openDeleteSkillModal: (skillId: string) => void;
  openAddSkillModal: () => void;
  existingCandidates: any[];
  isCandidatesExpanded: boolean;
  setIsCandidatesExpanded: (expanded: boolean) => void;
  candidateRows: Array<{ firstName: string; lastName: string; email: string; phone: string }>;
  candidateErrors: Array<{ firstName?: string; lastName?: string; email?: string; phone?: string }>;
  handleCandidateInputChange: (index: number, field: string, value: string) => void;
  handleAddCandidateRow: () => void;
  handleSendInvitations: () => void;
  candidatesLoading?: boolean;
  candidatesPage?: number;
  candidatesTotal?: number;
  candidatesItemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

const AssessmentDetail: React.FC<AssessmentDetailProps> = ({
  selectedAssessment,
  handleBackToAssessments,
  activeSidebarItem,
  setActiveSidebarItem,
  skills,
  openEditSkillModal,
  openDeleteSkillModal,
  openAddSkillModal,
  existingCandidates,
  isCandidatesExpanded,
  setIsCandidatesExpanded,
  candidateRows,
  candidateErrors,
  handleCandidateInputChange,
  handleAddCandidateRow,
  handleSendInvitations,
  candidatesLoading = false,
  candidatesPage = 1,
  candidatesTotal = 0,
  candidatesItemsPerPage = 10,
  onPageChange,
}) => {
  const navigate = useNavigate();
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                navigate('/dashboard', { replace: true });
                handleBackToAssessments();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {selectedAssessment.assessmentRole || selectedAssessment.title}
              </h1>
              <div className="flex items-center space-x-4 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedAssessment.status === 'active' ? 'bg-green-100 text-green-800' : selectedAssessment.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                  {selectedAssessment.status === 'active' ? 'Active' : selectedAssessment.status === 'draft' ? 'Draft' : selectedAssessment.status}
                </span>
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <Zap className="h-4 w-4" />
                  <span>{selectedAssessment?.skills?.length || 0} Skills</span>
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{selectedAssessment.duration} mins</span>
                </div>
              </div>
            </div>
          </div>
          <button 
            onClick={() => {
              navigate('/dashboard', { replace: true });
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go to dashboard"
          >
            <MoreVertical className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-64 p-4">
          <div className="space-y-2">
          <button
              onClick={() => setActiveSidebarItem('candidates')}
              className={`w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-all ${
                activeSidebarItem === 'candidates'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Candidates</div>
                <div className="text-xs text-gray-500 mt-0.5">Invite people to hire.</div>
              </div>
            </button>
            <button
              onClick={() => setActiveSidebarItem('skills')}
              className={`w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-all ${
                activeSidebarItem === 'skills'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Zap className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Skills</div>
                <div className="text-xs text-gray-500 mt-0.5">Topics to cover in the interview.</div>
              </div>
            </button>
            
            <button
              onClick={() => setActiveSidebarItem('settings')}
              className={`w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-all ${
                activeSidebarItem === 'settings'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Settings className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Settings</div>
                <div className="text-xs text-gray-500 mt-0.5">Customize your assessment.</div>
              </div>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {activeSidebarItem === 'skills' ? (
              <SkillsSection
                skills={skills}
                openEditSkillModal={openEditSkillModal}
                openDeleteSkillModal={openDeleteSkillModal}
                openAddSkillModal={openAddSkillModal}
                onNavigateToSettings={() => setActiveSidebarItem('settings')}
              />
            ) : activeSidebarItem === 'candidates' ? (
              <CandidatesInviteSection
                assessment={selectedAssessment}
                existingCandidates={existingCandidates}
                isCandidatesExpanded={isCandidatesExpanded}
                setIsCandidatesExpanded={setIsCandidatesExpanded}
                candidateRows={candidateRows}
                candidateErrors={candidateErrors}
                handleCandidateInputChange={handleCandidateInputChange}
                handleAddCandidateRow={handleAddCandidateRow}
                handleSendInvitations={handleSendInvitations}
                candidatesLoading={candidatesLoading}
                candidatesPage={candidatesPage}
                candidatesTotal={candidatesTotal}
                candidatesItemsPerPage={candidatesItemsPerPage}
                onPageChange={onPageChange}
              />
            ) : (
              <SettingsSection selectedAssessment={selectedAssessment} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetail;
